import type { User } from "./user.js";
import { ProfessionalStatus, ProfessionalType, type Professional } from "./professional.js";

/**
 * PLH (Profissional Legalmente Habilitado) — gate de aprovação e assinatura.
 *
 * Os perfis ENGINEER e ADMIN nunca concedem isto automaticamente: ver
 * PLH_SPECIFIC_PERMISSIONS em built-in-role-policy.ts. Nenhuma certificação é
 * inventada — cada verificação lê um campo que já existe no modelo.
 *
 * FONTE ÚNICA DA HABILITAÇÃO: O CLIENTE (decisão de 2026-09-24).
 *
 * A habilitação é decidida por `Client.responsiblePlhProfessionalId` — quem o
 * Cliente nomeou. Antes, o gate comparava `Professional.plhClientId`, um
 * segundo registro do mesmo fato, mantido no lado errado da relação. Isso
 * tinha duas consequências ruins:
 *
 *   - um engenheiro só podia ser PLH de UM Cliente, porque o campo guarda um
 *     valor só. Nomeá-lo num segundo Cliente movia a habilitação e fazia as
 *     aprovações do primeiro pararem de passar;
 *   - existiam duas fontes para a mesma verdade, que podiam divergir em
 *     silêncio.
 *
 * Com o Cliente como fonte, a cardinalidade sai de graça e é impossível de
 * violar: `responsiblePlhProfessionalId` é UM campo no Cliente, então um
 * Cliente nunca tem dois PLH; e quantos Clientes quiserem podem apontar para
 * o mesmo Professional.
 *
 * `Professional.plhClientId` passou a LEGADO — ver `plh-legacy-conflicts.ts`.
 */
export interface PlhEligibilityResult {
  readonly allowed: boolean;
  /** Presente só quando `allowed` é false — diz exatamente qual requisito faltou. */
  readonly reason?: string;
}

/**
 * O PLH responsável de um Cliente, como o gate precisa recebê-lo.
 *
 * Quem chama resolve isto pelo `ProjectResponsibilityService` (o resolvedor
 * central da seção 24) ou lendo o Cliente. O gate continua PURO e SÍNCRONO:
 * recebe o fato já resolvido, não vai buscá-lo.
 */
export type ClientResponsiblePlhId = string | null | undefined;

export function canActAsPlh(
  user: User | null,
  professional: Professional | null,
  clientResponsiblePlhProfessionalId: ClientResponsiblePlhId
): PlhEligibilityResult {
  if (!user) {
    return { allowed: false, reason: "Usuário autenticado não encontrado." };
  }
  if (!user.professionalId) {
    return { allowed: false, reason: "O usuário não possui um Profissional vinculado (User.professionalId ausente)." };
  }
  if (!professional) {
    return { allowed: false, reason: "O Profissional vinculado ao usuário não foi encontrado no cadastro." };
  }
  return isPlhQualifiedProfessional(professional, clientResponsiblePlhProfessionalId);
}

/**
 * A parte do gate que depende só do Profissional. É o que uma tela usa para
 * conferir um candidato já nomeado. Mesmos critérios de `canActAsPlh` — não
 * existe um segundo gate.
 */
export function isPlhQualifiedProfessional(
  professional: Professional,
  clientResponsiblePlhProfessionalId: ClientResponsiblePlhId
): PlhEligibilityResult {
  if (professional.status !== ProfessionalStatus.Active) {
    return { allowed: false, reason: "O Profissional vinculado está INATIVO no cadastro." };
  }
  if (professional.professionalType !== ProfessionalType.Engineer) {
    return { allowed: false, reason: "O Profissional vinculado não está cadastrado com função ENGINEER (PLH)." };
  }
  if (!clientResponsiblePlhProfessionalId) {
    return { allowed: false, reason: "O Cliente deste documento não possui um Engenheiro PLH responsável definido." };
  }
  if (String(professional.id) !== String(clientResponsiblePlhProfessionalId)) {
    return { allowed: false, reason: "O Profissional não é o PLH responsável definido no cadastro deste Cliente." };
  }
  if (!professional.signatureImageId) {
    return { allowed: false, reason: "O Profissional não possui imagem de assinatura cadastrada (signatureImageId ausente)." };
  }
  return { allowed: true };
}

/**
 * Habilitação de um Professional para um Cliente, sem ter o Cliente em mãos:
 * só os requisitos que dependem da pessoa. Serve para OFERECER candidatos num
 * seletor — a nomeação em si é que decide quem é o responsável.
 *
 * Deliberadamente NÃO responde "este é o PLH do Cliente X": isso é do
 * Cliente, e responder aqui recriaria a segunda fonte que esta mudança
 * eliminou.
 */
export function isEligibleToBeNamedPlh(professional: Professional): PlhEligibilityResult {
  if (professional.deletedAt) {
    return { allowed: false, reason: "O Profissional foi excluído do cadastro." };
  }
  if (professional.status !== ProfessionalStatus.Active) {
    return { allowed: false, reason: "O Profissional está INATIVO no cadastro." };
  }
  if (professional.professionalType !== ProfessionalType.Engineer) {
    return { allowed: false, reason: "O Profissional não está cadastrado com função ENGINEER (PLH)." };
  }
  if (!professional.signatureImageId) {
    return { allowed: false, reason: "O Profissional não possui imagem de assinatura cadastrada." };
  }
  return { allowed: true };
}
